import { useState, useEffect, useMemo } from 'react';
import type { AuthUser } from 'aws-amplify/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getProducts, createOrder, type Product, type CreatedOrder } from '../services/api';
import type { UseCartReturn } from '../hooks/useCart';
import { QUANTITY_DISCOUNT_THRESHOLD, QUANTITY_DISCOUNT_AMOUNT } from '../hooks/useCart';
import CartSummary from './CartSummary';
import OrderSuccessModal from './OrderSuccessModal';
import {
  Search,
  X,
  ChevronLeft,
  AlertCircle,
  RotateCcw,
  Package,
  ShoppingCart,
  ArrowRight,
  ZoomIn,
  Loader2,
  Info,
} from 'lucide-react';

interface DashboardProps {
  user: AuthUser | undefined;
  cart: UseCartReturn;
}

type StockFilter = 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';

export default function Dashboard({ user, cart }: DashboardProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<StockFilter>('in-stock');
  const [showCategoryGrid, setShowCategoryGrid] = useState(true);

  const [successOrder, setSuccessOrder] = useState<CreatedOrder | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt: string } | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProducts();
      setProducts(response.products || []);
      setProductTypes(response.productTypes || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const searchLower = searchQuery.toLowerCase().trim();
      if (searchLower) {
        const matchesTitle = product.title.toLowerCase().includes(searchLower);
        const matchesSku = product.variants.some(v =>
          v.sku?.toLowerCase().includes(searchLower)
        );
        if (!matchesTitle && !matchesSku) return false;
      }
      if (selectedCategory !== 'all' && product.productType !== selectedCategory) return false;
      const totalStock = product.variants.reduce((sum, v) => sum + v.inventoryQuantity, 0);
      if (stockFilter === 'in-stock' && totalStock === 0) return false;
      if (stockFilter === 'out-of-stock' && totalStock > 0) return false;
      if (stockFilter === 'low-stock' && (totalStock === 0 || totalStock > 10)) return false;
      return true;
    });
  }, [products, searchQuery, selectedCategory, stockFilter]);

  const categoryStats = useMemo(() => {
    return productTypes.map((type) => {
      const categoryProducts = products.filter((p) => p.productType === type);
      const inStockCount = categoryProducts.filter((p) =>
        p.variants.some((v) => v.inventoryQuantity > 0)
      ).length;
      const image = categoryProducts.find((p) => p.imageUrl)?.imageUrl || '';
      return { type, total: categoryProducts.length, inStock: inStockCount, image };
    });
  }, [products, productTypes]);

  const handleCategorySelect = (type: string) => {
    setSelectedCategory(type);
    setShowCategoryGrid(false);
  };

  const handleBackToCategories = () => {
    setSelectedCategory('all');
    setSearchQuery('');
    setShowCategoryGrid(true);
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || stockFilter !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setStockFilter('all');
  };

  const handleQuantityChange = (product: Product, variantId: string, value: string) => {
    const variant = product.variants.find(v => v.id === variantId);
    if (!variant) return;
    const quantity = parseInt(value, 10);
    if (isNaN(quantity) || quantity < 0) {
      cart.removeItem(variantId);
      return;
    }
    const clampedQuantity = Math.min(quantity, variant.inventoryQuantity);
    if (clampedQuantity > 0) {
      cart.addItem(product, variant, clampedQuantity);
    } else {
      cart.removeItem(variantId);
    }
  };

  const handleSubmitOrder = async () => {
    if (cart.isEmpty || !cart.summary.meetsMinimum) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await createOrder({
        items: cart.getOrderItems(),
        note: `Wholesale Portal Order`,
      });
      setSuccessOrder(response.order);
      setShowSuccessModal(true);
      cart.clearCart();
    } catch (err) {
      console.error('Error submitting order:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setSuccessOrder(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="mx-auto"
          >
            <Loader2 className="w-10 h-10 text-accent" />
          </motion.div>
          <p className="mt-4 text-text-muted uppercase tracking-wide text-sm">
            Loading wholesale catalog...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card text-center py-12"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-900/30 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-xl font-display text-cream mb-2">Unable to Load Products</h2>
        <p className="text-text-muted mb-6">{error}</p>
        <button onClick={fetchProducts} className="btn-primary inline-flex items-center gap-2">
          <RotateCcw className="w-4 h-4" />
          Try Again
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header with Stats */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl md:text-3xl font-display text-cream"
            >
              Wholesale Catalog
            </motion.h1>
            <p className="text-text-muted mt-1">
              Welcome back, {user?.signInDetails?.loginId}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="flex gap-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-workshop-surface px-4 py-3 rounded-sm shadow-soft border border-white/[0.06] flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-sm bg-white/[0.05] flex items-center justify-center">
                <Package className="w-4 h-4 text-text-muted" />
              </div>
              <div>
                <p className="text-[10px] text-text-muted uppercase tracking-wider">Products</p>
                <p className="text-lg font-bold text-cream leading-tight">{products.length}</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="bg-workshop-surface px-4 py-3 rounded-sm shadow-soft border border-white/[0.06] flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-sm bg-accent/10 flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-accent" />
              </div>
              <div>
                <p className="text-[10px] text-text-muted uppercase tracking-wider">In Cart</p>
                <p className="text-lg font-bold text-accent leading-tight">{cart.summary.itemCount}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Category Grid View */}
      {showCategoryGrid && !searchQuery ? (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-display text-cream tracking-wide">
              Shop by Category
            </h2>
            <button
              onClick={() => { setSelectedCategory('all'); setShowCategoryGrid(false); }}
              className="text-sm text-accent hover:text-accent-light uppercase tracking-wide font-medium
                         flex items-center gap-1.5 transition-colors"
            >
              View All Products
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Search Bar on Category View */}
          <div className="mb-5">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-text-muted group-focus-within:text-accent transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search by product name or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-white/10 rounded-sm bg-workshop-surface text-text
                           placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                           transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-cream"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categoryStats.map((cat, idx) => (
              <motion.button
                key={cat.type}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.06 }}
                whileHover={{ y: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCategorySelect(cat.type)}
                className="relative overflow-hidden rounded-sm border border-white/[0.06] bg-workshop-surface
                           text-left transition-all duration-300 group cursor-pointer
                           hover:border-accent/30 hover:shadow-lift"
              >
                {cat.image && (
                  <div className="w-full h-36 overflow-hidden bg-workshop">
                    <img
                      src={cat.image}
                      alt={cat.type}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-workshop-surface via-workshop-surface/40 to-transparent" />
                  </div>
                )}
                <div className="relative p-4">
                  <h3 className="font-display text-cream tracking-wide text-sm group-hover:text-accent transition-colors">
                    {cat.type}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-text-muted">
                      {cat.total} product{cat.total !== 1 ? 's' : ''}
                    </span>
                    <span className="text-xs text-green-400 font-medium bg-green-900/20 px-1.5 py-0.5 rounded-sm">
                      {cat.inStock} in stock
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Color Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 flex items-start gap-3 bg-amber-900/20 border border-amber-700/30 rounded-sm px-4 py-3 text-sm text-amber-200"
          >
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-400" />
            <p>
              Product images may not represent the exact color or style of the variant listed.
              Please refer to the product and variant description for accurate details.
            </p>
          </motion.div>

          {/* Search and Filters */}
          <div className="mb-6 bg-workshop-surface rounded-sm border border-white/[0.06] p-4 shadow-soft">
            <div className="flex flex-col lg:flex-row gap-3">
              {/* Back to Categories */}
              <button
                onClick={handleBackToCategories}
                className="lg:w-auto px-4 py-2.5 text-sm text-text-muted hover:text-accent border border-white/10
                           rounded-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Categories
              </button>

              {/* Search Bar */}
              <div className="flex-1">
                <label htmlFor="search" className="sr-only">Search products</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-text-muted group-focus-within:text-accent transition-colors" />
                  </div>
                  <input
                    type="text"
                    id="search"
                    placeholder="Search by product name or SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2.5 border border-white/10 rounded-sm bg-workshop text-text
                               placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-cream"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Category Filter */}
              <div className="lg:w-48">
                <label htmlFor="category" className="sr-only">Category</label>
                <select
                  id="category"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="block w-full py-2.5 px-3 border border-white/10 rounded-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent bg-workshop text-text"
                >
                  <option value="all">All Categories</option>
                  {productTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Stock Filter */}
              <div className="lg:w-40">
                <label htmlFor="stock" className="sr-only">Stock Status</label>
                <select
                  id="stock"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value as StockFilter)}
                  className="block w-full py-2.5 px-3 border border-white/10 rounded-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent bg-workshop text-text"
                >
                  <option value="all">All Stock</option>
                  <option value="in-stock">In Stock</option>
                  <option value="low-stock">Low Stock</option>
                  <option value="out-of-stock">Out of Stock</option>
                </select>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="lg:w-auto px-4 py-2.5 text-sm text-text-muted hover:text-accent border border-white/10
                             rounded-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>

            {/* Filter Results Info */}
            {hasActiveFilters && (
              <div className="mt-3 pt-3 border-t border-white/[0.06] text-sm text-text-muted">
                Showing {filteredProducts.length} of {products.length} products
                {searchQuery && <span className="ml-1">matching "{searchQuery}"</span>}
                {selectedCategory !== 'all' && <span className="ml-1">in {selectedCategory}</span>}
                {stockFilter !== 'all' && <span className="ml-1">({stockFilter.replace('-', ' ')})</span>}
              </div>
            )}
          </div>
        </>
      )}

      {/* Product list view */}
      {(!showCategoryGrid || searchQuery) && <>

      {/* Submit Error Alert */}
      <AnimatePresence>
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 bg-red-950/50 border border-red-800/30 rounded-sm p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-red-300">Order Submission Failed</p>
              <p className="text-sm text-red-400 mt-1">{submitError}</p>
            </div>
            <button
              onClick={() => setSubmitError(null)}
              className="text-red-400 hover:text-red-300"
            >
              <X className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Products */}
      {products.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-muted">No products available at this time.</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
            <Search className="w-8 h-8 text-text-muted" />
          </div>
          <h3 className="text-lg font-medium text-cream mb-2">No products found</h3>
          <p className="text-text-muted mb-4">No products match your current filters.</p>
          <button onClick={clearFilters} className="btn-secondary inline-flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            Clear Filters
          </button>
        </div>
      ) : (
        <>
        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {filteredProducts.map((product) =>
            product.variants.map((variant, variantIndex) => {
              const cartQuantity = cart.getQuantity(variant.id);
              const subtotal = cartQuantity * parseFloat(variant.wholesalePrice);
              const inCart = cartQuantity > 0;

              return (
                <motion.div
                  key={variant.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: variantIndex * 0.02 }}
                  className={`
                    bg-workshop-surface rounded-sm border p-3 shadow-soft transition-all
                    ${inCart ? 'ring-1 ring-accent/50 border-accent/20 bg-accent/[0.03]' : 'border-white/[0.06]'}
                  `}
                >
                  <div className="flex gap-3">
                    {(variant.imageUrl || product.imageUrl) && (
                      <button
                        type="button"
                        onClick={() => setLightboxImage({
                          url: variant.imageUrl || product.imageUrl!,
                          alt: variant.title !== 'Default Title'
                            ? `${product.title} - ${variant.title}`
                            : product.title,
                        })}
                        className="flex-shrink-0 cursor-zoom-in relative group"
                      >
                        <img
                          src={variant.imageUrl || product.imageUrl!}
                          alt={product.title}
                          className="w-16 h-16 object-cover rounded-sm border border-white/10 group-hover:border-accent transition-colors"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-sm">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-cream text-sm truncate">{product.title}</p>
                      {variant.title !== 'Default Title' && (
                        <p className="text-xs text-text-muted truncate">{variant.title}</p>
                      )}
                      {variantIndex === 0 && (() => {
                        const qtyDiscountEnabled = product.settings?.quantityDiscountEnabled !== false;
                        const threshold = product.settings?.quantityDiscountThreshold ?? QUANTITY_DISCOUNT_THRESHOLD;
                        const amount = product.settings?.quantityDiscountAmount ?? QUANTITY_DISCOUNT_AMOUNT;
                        if (qtyDiscountEnabled && amount > 0) {
                          return (
                            <p className="text-xs text-green-400 mt-0.5">
                              Order {threshold}+ &amp; save ${amount.toFixed(2)} each
                            </p>
                          );
                        }
                        return null;
                      })()}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-text-muted line-through">${parseFloat(variant.price).toFixed(2)}</span>
                        <span className="text-sm font-semibold text-accent">${variant.wholesalePrice}</span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${
                            variant.inventoryQuantity > 10
                              ? 'bg-green-900/40 text-green-300'
                              : variant.inventoryQuantity > 0
                              ? 'bg-yellow-900/40 text-yellow-300'
                              : 'bg-red-900/40 text-red-300'
                          }`}
                        >
                          {variant.inventoryQuantity > 0 ? `${variant.inventoryQuantity} in stock` : 'Out'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-text-muted uppercase tracking-wide">Qty:</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max={variant.inventoryQuantity}
                        value={cartQuantity || ''}
                        onChange={(e) => handleQuantityChange(product, variant.id, e.target.value)}
                        placeholder="0"
                        disabled={variant.inventoryQuantity === 0}
                        className={`
                          w-16 px-2 py-1.5 text-center border rounded-sm text-sm
                          bg-workshop text-text
                          focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                          disabled:bg-white/5 disabled:cursor-not-allowed disabled:text-text-muted
                          ${inCart ? 'border-accent bg-accent/10' : 'border-white/10'}
                        `}
                      />
                    </div>
                    {inCart && (
                      <span className="font-semibold text-accent text-sm">
                        ${subtotal.toFixed(2)}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Desktop Table View */}
        <div className="bg-workshop-surface rounded-sm border border-white/[0.06] overflow-hidden shadow-soft hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-workshop border-b border-white/[0.08]">
                  <th className="px-4 py-3.5 text-left uppercase text-[11px] tracking-wider font-semibold text-text-muted">Product</th>
                  <th className="px-4 py-3.5 text-left uppercase text-[11px] tracking-wider font-semibold text-text-muted">Category</th>
                  <th className="px-4 py-3.5 text-left uppercase text-[11px] tracking-wider font-semibold text-text-muted hidden lg:table-cell">SKU</th>
                  <th className="px-4 py-3.5 text-right uppercase text-[11px] tracking-wider font-semibold text-text-muted">Retail</th>
                  <th className="px-4 py-3.5 text-right uppercase text-[11px] tracking-wider font-semibold text-accent">Wholesale</th>
                  <th className="px-4 py-3.5 text-center uppercase text-[11px] tracking-wider font-semibold text-text-muted">Stock</th>
                  <th className="px-4 py-3.5 text-center uppercase text-[11px] tracking-wider font-semibold text-text-muted">Qty</th>
                  <th className="px-4 py-3.5 text-right uppercase text-[11px] tracking-wider font-semibold text-text-muted">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) =>
                  product.variants.map((variant, variantIndex) => {
                    const cartQuantity = cart.getQuantity(variant.id);
                    const subtotal = cartQuantity * parseFloat(variant.wholesalePrice);
                    const inCart = cartQuantity > 0;

                    return (
                      <motion.tr
                        key={variant.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: variantIndex * 0.015 }}
                        className={`
                          border-b border-white/[0.04] transition-all duration-200
                          ${inCart
                            ? 'bg-accent/[0.04] hover:bg-accent/[0.07]'
                            : 'bg-workshop-surface hover:bg-workshop-elevated'
                          }
                        `}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center">
                            {(variant.imageUrl || product.imageUrl) && (
                              <button
                                type="button"
                                onClick={() => setLightboxImage({
                                  url: variant.imageUrl || product.imageUrl!,
                                  alt: variant.title !== 'Default Title'
                                    ? `${product.title} - ${variant.title}`
                                    : product.title,
                                })}
                                className="flex-shrink-0 cursor-zoom-in relative group"
                              >
                                <img
                                  src={variant.imageUrl || product.imageUrl!}
                                  alt={variant.imageUrl ? `${product.title} - ${variant.title}` : product.title}
                                  className="w-11 h-11 object-cover rounded-sm mr-3 border border-white/10
                                             group-hover:border-accent group-hover:shadow transition-all"
                                />
                                <div className="absolute inset-0 mr-3 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-sm">
                                  <ZoomIn className="w-3.5 h-3.5 text-white" />
                                </div>
                              </button>
                            )}
                            <div>
                              <p className="font-medium text-cream text-sm">{product.title}</p>
                              {variant.title !== 'Default Title' && (
                                <p className="text-xs text-text-muted">{variant.title}</p>
                              )}
                              {variantIndex === 0 && (() => {
                                const qtyDiscountEnabled = product.settings?.quantityDiscountEnabled !== false;
                                const threshold = product.settings?.quantityDiscountThreshold ?? QUANTITY_DISCOUNT_THRESHOLD;
                                const amount = product.settings?.quantityDiscountAmount ?? QUANTITY_DISCOUNT_AMOUNT;
                                if (qtyDiscountEnabled && amount > 0) {
                                  return (
                                    <p className="text-[11px] text-green-400 mt-0.5 font-medium">
                                      Order {threshold}+ &amp; save ${amount.toFixed(2)} each
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex px-2 py-1 text-[11px] rounded-sm bg-white/[0.04] text-text-muted font-medium border border-white/[0.04]">
                            {product.productType}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-text-muted font-mono text-xs hidden lg:table-cell">
                          {variant.sku || '-'}
                        </td>
                        <td className="px-4 py-3.5 text-text-muted line-through text-right text-sm">
                          ${parseFloat(variant.price).toFixed(2)}
                        </td>
                        <td className="px-4 py-3.5 text-accent font-semibold text-right text-sm">
                          ${variant.wholesalePrice}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <span
                            className={`inline-flex px-2 py-0.5 text-[11px] rounded-sm font-medium ${
                              variant.inventoryQuantity > 10
                                ? 'bg-green-900/30 text-green-300'
                                : variant.inventoryQuantity > 0
                                ? 'bg-yellow-900/30 text-yellow-300'
                                : 'bg-red-900/30 text-red-300'
                            }`}
                          >
                            {variant.inventoryQuantity > 0 ? variant.inventoryQuantity : 'Out'}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max={variant.inventoryQuantity}
                            value={cartQuantity || ''}
                            onChange={(e) => handleQuantityChange(product, variant.id, e.target.value)}
                            placeholder="0"
                            disabled={variant.inventoryQuantity === 0}
                            className={`
                              w-20 px-3 py-2 text-center border rounded-sm transition-all text-sm
                              bg-workshop text-text
                              focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                              disabled:bg-white/5 disabled:cursor-not-allowed disabled:text-text-muted
                              ${inCart ? 'border-accent bg-accent/10' : 'border-white/10'}
                            `}
                          />
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          {inCart ? (
                            <span className="font-semibold text-accent text-sm">
                              ${subtotal.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-text-muted">-</span>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Cart Summary */}
      <CartSummary
        summary={cart.summary}
        items={cart.items}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitOrder}
        onClear={cart.clearCart}
      />

      {/* Success Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        order={successOrder}
        onClose={handleCloseSuccess}
      />

      </>}

      {/* Display Case Program Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8"
      >
        <Link to="/display-cases" className="block group">
          <div className="relative overflow-hidden rounded-sm border border-white/[0.06] bg-workshop-surface p-6
                          hover:border-accent/20 hover:shadow-lift transition-all duration-300">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent/[0.03] rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-display text-accent tracking-wide mb-1 group-hover:text-accent-light transition-colors">
                  Display Case Rebate Program
                </h3>
                <p className="text-text-muted text-sm">
                  Showcase Top Dog Brand in your store and earn back the cost of your display case through rebates on every reorder.
                </p>
              </div>
              <div className="flex-shrink-0">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 border border-accent text-accent text-sm uppercase tracking-wide
                               rounded-sm hover:bg-accent hover:text-workshop transition-all duration-200 group-hover:translate-x-1">
                  Learn More
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Image Lightbox Modal */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setLightboxImage(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-2xl max-h-[80vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setLightboxImage(null)}
                className="absolute -top-10 right-0 text-white/80 hover:text-white text-sm uppercase tracking-wide flex items-center gap-1.5"
              >
                Close
                <X className="w-4 h-4" />
              </button>
              <img
                src={lightboxImage.url}
                alt={lightboxImage.alt}
                className="w-full max-h-[75vh] object-contain rounded-sm bg-workshop-surface shadow-2xl"
              />
              <p className="text-center text-white/70 text-sm mt-3">{lightboxImage.alt}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
